// Build calendar links for an event. Times are converted from the venue's IANA
// timezone (event.timezone) to UTC so the event lands at the correct local time
// in the recipient's calendar, regardless of their zone. Falls back to floating
// local time when no timezone is known, and to an all-day event when no time is set.
import { wallToUtc } from "./timezone";

function pad(n) { return String(n).padStart(2, "0"); }
function toYMDdash(iso) {
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(iso || "").trim());
    return m ? `${m[1]}-${pad(m[2])}-${pad(m[3])}` : null;
}
function toYMD(iso) { const d = toYMDdash(iso); return d ? d.replace(/-/g, "") : null; }
function nextDay(ymd) {
    const y = +ymd.slice(0, 4), mo = +ymd.slice(4, 6) - 1, d = +ymd.slice(6, 8);
    const dt = new Date(Date.UTC(y, mo, d + 1));
    return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}`;
}
function parseTimeLabel(label) {
    const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(label || "").trim());
    if (!m) return null;
    let h = (+m[1]) % 12;
    if (/PM/i.test(m[3])) h += 12;
    return h * 60 + (+m[2]);
}
function toICSUtc(dt) {
    return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}00Z`;
}
const hms = (min) => `${pad(Math.floor(min / 60))}${pad(min % 60)}00`;
const hmsDash = (min) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}:00`;

// The timed span (first start → last end) across single- or multi-day events.
function timedSpan(ev) {
    const days = Array.isArray(ev.day_times) ? ev.day_times : [];
    if (days.length) {
        let sYMD = null, sMin = null, eYMD = null, eMin = null;
        for (const d of days) { const mm = parseTimeLabel(d.start); if (mm != null) { sYMD = toYMDdash(d.date); sMin = mm; break; } }
        if (sYMD == null) return null;                 // no start times → treat as all-day
        // End: the LAST day carrying any time — prefer its end, else its start + 1h —
        // so a final day with only a start still extends the span to that day.
        for (let i = days.length - 1; i >= 0; i--) {
            const em = parseTimeLabel(days[i].end);
            const sm = parseTimeLabel(days[i].start);
            if (em != null) { eYMD = toYMDdash(days[i].date); eMin = em; break; }
            if (sm != null) { eYMD = toYMDdash(days[i].date); eMin = sm + 60; break; }
        }
        if (eYMD == null) { eYMD = sYMD; eMin = sMin + 60; }
        return { sYMD, sMin, eYMD, eMin };
    }
    const sMin = parseTimeLabel(ev.start_time);
    if (sMin == null) return null;
    const sYMD = toYMDdash(ev.event_date);
    let eYMD = toYMDdash(ev.end_date) || sYMD;
    let eMin = parseTimeLabel(ev.end_time);
    if (eMin == null) eMin = sMin + 60;
    if (eYMD === sYMD && eMin <= sMin) eMin = sMin + 60;
    return { sYMD, sMin, eYMD, eMin };
}

export function calendarData(ev) {
    const start = toYMD(ev.event_date);
    if (!start) return null;
    const location = [
        ev.location_name, ev.street_address,
        [ev.city, ev.state].filter(Boolean).join(", "), ev.zip,
    ].filter(Boolean).join(", ");
    const details = [
        ev.sport && ev.type ? `${ev.sport} · ${ev.type}` : "",
        ev.description || "",
    ].filter(Boolean).join("\n");
    const title = ev.name || "Event";
    const span = timedSpan(ev);
    const tz = ev.timezone;

    if (span && tz) {
        const sU = wallToUtc(tz, span.sYMD, span.sMin);
        let eU = wallToUtc(tz, span.eYMD, span.eMin);
        if (eU <= sU) eU = new Date(sU.getTime() + 60 * 60000);
        return { timed: true, tz, title, location, details, sU, eU };
    }
    if (span) {
        // No timezone known — floating local time.
        return {
            timed: true, floating: true, title, location, details,
            gStart: `${span.sYMD.replace(/-/g, "")}T${hms(span.sMin)}`,
            gEnd: `${span.eYMD.replace(/-/g, "")}T${hms(span.eMin)}`,
            startDash: `${span.sYMD}T${hmsDash(span.sMin)}`,
            endDash: `${span.eYMD}T${hmsDash(span.eMin)}`,
        };
    }
    const endBase = toYMD(ev.end_date) || start;
    return { timed: false, title, location, details, start, endExcl: nextDay(endBase) };
}

export function googleCalUrl(ev) {
    const c = calendarData(ev);
    if (!c) return null;
    let dates;
    if (c.timed && c.tz) dates = `${toICSUtc(c.sU)}/${toICSUtc(c.eU)}`;
    else if (c.timed) dates = `${c.gStart}/${c.gEnd}`;
    else dates = `${c.start}/${c.endExcl}`;
    const p = new URLSearchParams({ action: "TEMPLATE", text: c.title, dates, details: c.details, location: c.location });
    return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

export function outlookCalUrl(ev) {
    const c = calendarData(ev);
    if (!c) return null;
    const isoDash = (ymd) => `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
    let startdt, enddt, allday;
    if (c.timed && c.tz) { startdt = c.sU.toISOString(); enddt = c.eU.toISOString(); allday = "false"; }
    else if (c.timed) { startdt = c.startDash; enddt = c.endDash; allday = "false"; }
    else { startdt = isoDash(c.start); enddt = isoDash(c.endExcl); allday = "true"; }
    const p = new URLSearchParams({
        path: "/calendar/action/compose", rru: "addevent", subject: c.title,
        startdt, enddt, body: c.details, location: c.location, allday,
    });
    return `https://outlook.live.com/calendar/0/deeplink/compose?${p.toString()}`;
}

// Object URL for a downloadable .ics (Apple Calendar / Outlook desktop).
export function icsBlobUrl(ev) {
    const c = calendarData(ev);
    if (!c) return null;
    const esc = (s) => String(s || "").replace(/([,;\\])/g, "\\$1").replace(/\r?\n/g, "\\n");
    const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    let se;
    if (c.timed && c.tz) se = [`DTSTART:${toICSUtc(c.sU)}`, `DTEND:${toICSUtc(c.eU)}`];
    else if (c.timed) se = [`DTSTART:${c.gStart}`, `DTEND:${c.gEnd}`];
    else se = [`DTSTART;VALUE=DATE:${c.start}`, `DTEND;VALUE=DATE:${c.endExcl}`];
    const lines = [
        "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//TravelSportsInfo//EN", "CALSCALE:GREGORIAN",
        "BEGIN:VEVENT", `UID:${Date.now()}@travelsportsinfo`, `DTSTAMP:${stamp}`,
        ...se,
        `SUMMARY:${esc(c.title)}`, `LOCATION:${esc(c.location)}`, `DESCRIPTION:${esc(c.details)}`,
        "END:VEVENT", "END:VCALENDAR",
    ];
    const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
    return URL.createObjectURL(blob);
}


// The moment an event actually ends, as a JS Date (an absolute instant when the
// event's timezone is known). Returns null when it can't be determined.
// Used to open reviews right at the end of the event time (not the next day).
export function eventEndsAt(ev) {
    const c = calendarData(ev);
    if (!c) return null;
    if (c.timed && c.tz) return c.eU;                    // tz-aware end instant
    if (c.timed && c.endDash) return new Date(c.endDash); // floating → viewer-local wall time
    // All-day / no time known: fall back to local midnight starting the day after the last day.
    if (c.endExcl) {
        const y = +c.endExcl.slice(0, 4), mo = +c.endExcl.slice(4, 6) - 1, d = +c.endExcl.slice(6, 8);
        return new Date(y, mo, d, 0, 0, 0, 0);
    }
    return null;
}
